import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Container, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import heroImage from '../imgs/profile.png';
import '../styles/global.css';

const roles = [
  "Software Development Engineer",
  "Coffee -> Code -> Sleep -> Repeat",
  "I play chess",
  "Escape :wq",
];

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [roleIndex, setRoleIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let typingTimer;
    const currentFullText = roles[roleIndex];

    if (isDeleting) {
      if (displayText === '') {
        setIsDeleting(false);
        setRoleIndex((prev) => (prev + 1) % roles.length);
      } else {
        typingTimer = setTimeout(() => {
          setDisplayText(currentFullText.substring(0, displayText.length - 1));
        }, 50);
      }
    } else {
      if (displayText === currentFullText) {
        typingTimer = setTimeout(() => setIsDeleting(true), 2000);
      } else {
        typingTimer = setTimeout(() => {
          setDisplayText(currentFullText.substring(0, displayText.length + 1));
        }, 100);
      }
    }

    return () => clearTimeout(typingTimer);
  }, [displayText, isDeleting, roleIndex]);

  const handleMouseMove = (event) => {
    const { clientX, clientY } = event;
    const { innerWidth, innerHeight } = window;
    const offsetX = (clientX - innerWidth / 2) / 50;
    const offsetY = (clientY - innerHeight / 2) / 50;
    setMousePos({ x: offsetX, y: offsetY });
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const greetingVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const nameVariants = {
    hidden: { opacity: 0, filter: 'blur(10px)', y: 20 },
    visible: { 
      opacity: 1, 
      filter: 'blur(0px)', 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay: 0.2 }
    }
  };

    const skills = [
        'Java',
        'Spring Framework',
        'Spring Boot',
        'Vert.x',
        'React.js',
        'Node.js',
        'REST APIs',
        'Microservices',
        'Docker',
        'PostgreSQL',
        'MongoDB',
        'Redis',
        'RabbitMQ',
        'Git',
        'Linux',
        'IAM',
        'SSO',
        'OAuth 2.0',
        'SAML',
        'JWT',
        'Burp Suite'
    ];

  return (
    <Box 
      component="section"
      sx={{ 
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        background: 'transparent',
        pt: { xs: 4, md: 4 },
        pb: { xs: 6, md: 12 }
      }}
    >
      <Container 
        maxWidth="xl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 4, lg: 8 }
        }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            maxWidth: { xs: '100%', lg: '60%' },
            order: { xs: 2, lg: 1 }
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h1" 
              className="hero-greeting" 
              sx={{ 
                fontSize: { xs: '1.6rem', sm: '2.2rem', md: '3.5rem' },
                mb: 1,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <motion.span 
                variants={greetingVariants}
                initial="hidden"
                animate="visible"
                className="wave"
              >
                👋
              </motion.span>
              <motion.span
                variants={greetingVariants}
                initial="hidden"
                animate="visible"
                className="gradient-text"
              >
                Hey, I'm
              </motion.span>
            </Typography>
            <Typography 
              variant="h1" 
              className="hero-name" 
              sx={{ 
                fontSize: { xs: '2.8rem', sm: '4.5rem', md: '7.5rem' },
                mb: 2,
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}
            >
              <motion.span
                variants={nameVariants}
                initial="hidden"
                animate="visible"
                className="name-title"
                style={{ display: 'inline-block' }}
              >
                ONKAR WAGH
              </motion.span>
            </Typography>
            <Box sx={{ height: { xs: '50px', md: '80px' }, mb: 3 }}>
              <Typography 
                variant="h1"
                className="hero-role" 
                sx={{ 
                  fontSize: { xs: '1.3rem', sm: '1.8rem', md: '3rem' },
                  color: 'var(--primary)',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  opacity: 1
                }}
              >
                {displayText}
                <motion.span 
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "steps(2)" }}
                  className="typing-cursor"
                  style={{ color: 'var(--primary)' }}
                >
                  |
                </motion.span>
              </Typography>
            </Box>
            
            <Typography 
              variant="body1" 
              className="hero-description" 
              sx={{ 
                mt: { xs: 3, md: 5 }, 
                mb: { xs: 4, md: 6 },
                fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.35rem' },
                lineHeight: 1.7,
                color: '#ffffff',
                maxWidth: '850px',
                opacity: 0.9
              }}
            >
              I enjoy building reliable backend systems with a focus on security, scalability, and maintainability. My experience includes enterprise Identity & Access Management (IAM), modernizing legacy applications, and developing backend services using Java and Spring. Outside of work, I like exploring distributed systems, RAG applications, developer tools, and other technologies through hands-on experimentation and personal projects.
            </Typography>

            <Box 
              className="skills-section"
              sx={{
                mt: { xs: 3, md: 5 }
              }}
            >
              <Typography 
                variant="h6" 
                className="skills-title" 
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' },
                  fontWeight: 600,
                  color: '#fff'
                }}
              >
                Tech Stack
              </Typography>
              <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
              className="skills-container"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: isMobile ? '8px' : '12px'
              }}
            >
              {skills.map((skill, index) => (
                <motion.div
                  key={skill}
                  initial={{ opacity: 0, scale: 0.5, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ 
                    delay: index * 0.03 + 0.3,
                    type: "spring",
                    stiffness: 260,
                    damping: 20
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(8px)',
                    transition: 'all 0.3s ease',
                    cursor: 'default'
                  }}
                  whileHover={{
                    scale: 1.1,
                    y: -5,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 5px 15px rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {skill}
                </motion.div>
              ))}
            </motion.div>
          </Box>
        </motion.div>
      </Box>

      <Box
        sx={{
          flex: '0 0 auto',
          width: { xs: '100%', sm: '80%', md: '70%', lg: '40%' },
          order: { xs: 1, lg: 2 },
          display: { xs: 'none', lg: 'block' }
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            x: mousePos.x,
            y: mousePos.y
          }}
          transition={{ 
            duration: 0.6, 
            ease: "easeOut",
            x: { type: "spring", stiffness: 50, damping: 20 },
            y: { type: "spring", stiffness: 50, damping: 20 }
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            perspective: '1000px'
          }}
        >
          <motion.img
            src={heroImage}
            alt="Hero"
            className="hero-image"
            animate={{
              y: [0, -10, 0],
              rotateY: mousePos.x / 2,
              rotateX: -mousePos.y / 2
            }}
            transition={{
              y: {
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              },
              rotateY: { type: "spring", stiffness: 50, damping: 20 },
              rotateX: { type: "spring", stiffness: 50, damping: 20 }
            }}
            whileHover={{ scale: 1.05 }}
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '600px',
              objectFit: 'contain',
              filter: 'drop-shadow(0 20px 30px rgba(0, 0, 0, 0.4))',
              transition: 'filter 0.3s ease'
            }}
          />
        </motion.div>
      </Box>
      </Container>
    </Box>
  );
};

export default Hero; 